import * as React from "react";
import { Mic, MicOff } from "lucide-react";
import { Button, type ButtonProps } from "../primitives/Button";
import { cn } from "../lib/cn";

/**
 * MicButton — shared mic affordance for primitives that ship voice input.
 *
 * Encapsulates the icon swap (`Mic` ↔ `MicOff`), the aria semantics
 * (`aria-pressed` + listening-aware `aria-label`), and the theme tokens
 * for idle / hover / listening so every primitive that exposes voice
 * dictation reads as the same recording affordance:
 *
 *   - **Idle**:     `text-muted-foreground` · hover bumps to `bg-muted/50 text-foreground`
 *   - **Listening**: `bg-destructive/10 text-destructive` · hover stays destructive-tinted
 *
 * Listening uses the destructive token rather than accent because the
 * action a click performs while listening is "stop recording" — the
 * red read matches the user's mental model from native voice UIs.
 *
 * Press language (Onyx "offset key"): MicButton is a ROUND control, so it keeps
 * its circle — the hex notch is for rectangular surfaces. It still shares the
 * offset-key feel via the press: a springy 1px press-DOWN on `:active` (a CSS
 * translate + overshoot ease, the same bounce every Button/KeycapButton uses),
 * so the whole lib presses consistently. No notch, no travel block, no keyline
 * ring. The underlying Button stays `motion={false}` (its notch-oriented
 * diagonal press doesn't apply to a round face); the round press rides on
 * `className` instead.
 *
 * Composition: thin wrapper over pretty's `<Button size="icon" variant="ghost" motion={false}>`.
 * Consumers can still pass `className` to layer extras (e.g.
 * `<SmartSearch>` adds `transition-opacity` + `opacity-0` for SSR
 * feature-detect fade-in); twMerge handles the conflict resolution.
 *
 * State is intentionally controlled — every existing consumer
 * (`<ChatInput>`, `<SmartSearch>`) tracks `isListening` externally
 * (via `useVoiceInput` or its own recognizer), so MicButton stays
 * stateless and just renders the right chrome for the prop.
 */
/**
 * The shared offset-key press: a springy 1px press-DOWN with overshoot ease —
 * the round-control tier (no notch/block). Inlined locally so MicButton owns
 * its press without reaching into another primitive's internals.
 */
const MIC_PRESS =
  "transition-transform duration-150 ease-[cubic-bezier(0.34,1.45,0.6,1)] active:translate-y-px";

export interface MicButtonProps extends Omit<
  ButtonProps,
  "size" | "variant" | "motion" | "children" | "aria-pressed" | "aria-label" | "asChild"
> {
  /** Whether voice recognition is actively listening — drives icon + theme tokens. */
  listening: boolean;
  /** Aria label when not listening. Default: `"Start voice input"`. */
  startLabel?: string;
  /** Aria label when listening. Default: `"Stop listening"`. */
  stopLabel?: string;
}

export const MicButton = React.forwardRef<HTMLButtonElement, MicButtonProps>(
  (
    {
      listening,
      startLabel = "Start voice input",
      stopLabel = "Stop listening",
      className,
      ...rest
    },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        size="icon"
        variant="ghost"
        motion={false}
        type="button"
        aria-pressed={listening}
        aria-label={listening ? stopLabel : startLabel}
        className={cn(
          // Round control: keep the circle, drop the inherited hex notch clip.
          "h-8 w-8 rounded-full [clip-path:none]",
          // Shared offset-key press (round tier): 1px press-DOWN + springy bounce.
          MIC_PRESS,
          listening
            ? "bg-destructive/10 text-destructive hover:bg-destructive/15 hover:text-destructive"
            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
          className,
        )}
        {...rest}
      >
        {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </Button>
    );
  },
);

MicButton.displayName = "MicButton";
